import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Users, Shield, ShieldOff, Info, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Alert, AlertDescription } from './ui/alert';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingForm = () => {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfGuests: 1,
    weatherInsurance: 'no',
    message: ''
  });
  const [pricingKey, setPricingKey] = useState(0);

  // Force pricing recalculation when weather insurance changes
  useEffect(() => {
    if (selectedSlot) {
      setPricingKey(prev => prev + 1);
    }
  }, [formData.weatherInsurance, selectedSlot]);

  // Fetch availability when date is selected
  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoadingAvailability(true);

    try {
      const response = await axios.post(`${API}/availability`, {
        date: format(date, 'yyyy-MM-dd')
      });
      setAvailability(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast({
        title: "Erreur",
        description: "Impossible de récupérer les disponibilités",
        variant: "destructive"
      });
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWeatherInsuranceChange = (value) => {
    setFormData(prev => ({ ...prev, weatherInsurance: value }));
  };

  const calculateTotal = () => {
    if (!selectedSlot) return null;

    const basePrice = selectedSlot.price;
    const serviceFee = 10;
    const weatherIns = formData.weatherInsurance === 'yes' ? 17 : 0;
    const subtotal = basePrice + serviceFee + weatherIns;
    const deposit = Math.round(subtotal * 0.30 * 100) / 100;
    const remaining = Math.round((subtotal - deposit) * 100) / 100;

    return {
      basePrice,
      serviceFee,
      weatherInsurance: weatherIns,
      subtotal,
      deposit,
      remaining
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedSlot) {
      toast({
        title: "Information manquante",
        description: "Veuillez sélectionner une date et un créneau",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const originUrl = window.location.origin;
      
      const reservationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedSlot.slot,
        number_of_guests: parseInt(formData.numberOfGuests),
        weather_insurance: formData.weatherInsurance,
        message: formData.message || null,
        origin_url: originUrl
      };

      const response = await axios.post(`${API}/reservations`, reservationData);
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("URL de paiement non reçue");
      }

    } catch (error) {
      console.error('Error creating reservation:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de créer la réservation",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const total = calculateTotal();
  // Minimum demain à minuit (pour éviter les problèmes de comparaison d'heures)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = addDays(today, 1);

  return (
    <Card className="shadow-2xl border-2 max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-3xl flex items-center gap-3">
          <CalendarIcon className="w-8 h-8 text-blue-600" />
          Réservation en ligne
        </CardTitle>
        <CardDescription className="text-lg">
          Sélectionnez votre date et créneau horaire pour réserver votre sortie en mer
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Calendar Selection */}
          <div>
            <Label className="text-lg font-semibold mb-3 block">1. Choisissez votre date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-14 text-lg border-2"
                >
                  <CalendarIcon className="mr-2 h-5 w-5" />
                  {selectedDate ? format(selectedDate, 'PPP', { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date < minDate}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div>
              <Label className="text-lg font-semibold mb-3 block">2. Sélectionnez votre créneau</Label>
              {loadingAvailability ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">Chargement des disponibilités...</span>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {availability?.slots.map((slot) => (
                    <button
                      key={slot.slot}
                      type="button"
                      onClick={() => slot.available && setSelectedSlot(slot)}
                      disabled={!slot.available}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        selectedSlot?.slot === slot.slot
                          ? 'border-blue-600 bg-blue-50 shadow-lg'
                          : slot.available
                          ? 'border-gray-200 hover:border-blue-400 hover:shadow-md'
                          : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <div className="font-semibold text-lg mb-1">{slot.slot}</div>
                      <div className="text-sm text-gray-600 mb-2">{slot.time}</div>
                      <div className="font-bold text-blue-600">{slot.price}€</div>
                      {!slot.available && (
                        <div className="text-red-600 text-sm mt-2 font-medium">Non disponible</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Guest Information */}
          {selectedSlot && (
            <>
              <div className="border-t pt-6">
                <Label className="text-lg font-semibold mb-4 block">3. Vos informations</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="jean.dupont@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="06 12 34 56 78"
                    />
                  </div>

                  <div>
                    <Label htmlFor="numberOfGuests">Nombre de personnes *</Label>
                    <Input
                      id="numberOfGuests"
                      name="numberOfGuests"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.numberOfGuests}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="message">Message ou demandes spéciales</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="mt-2"
                    placeholder="Prestations culinaires souhaitées, allergies, demandes particulières..."
                  />
                </div>
              </div>

              {/* Weather Insurance */}
              <div className="border-t pt-6">
                <Label className="text-lg font-semibold mb-3 block">4. Assurance météo</Label>
                <RadioGroup value={formData.weatherInsurance} onValueChange={handleWeatherInsuranceChange}>
                  <div className="flex items-start space-x-3 p-4 border-2 border-blue-600 rounded-xl bg-blue-50">
                    <RadioGroupItem value="yes" id="insurance-yes" className="mt-1" />
                    <label htmlFor="insurance-yes" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 font-semibold text-blue-900 mb-2">
                        <Shield className="w-5 h-5" />
                        Garantie météo - 17€/jour
                      </div>
                      <p className="text-sm text-blue-800">
                        Soyez intégralement remboursé si la météo ne vous permet pas de naviguer 
                        pour cause de bulletin météo spécial. Remboursement après confirmation du propriétaire.
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-xl">
                    <RadioGroupItem value="no" id="insurance-no" className="mt-1" />
                    <label htmlFor="insurance-no" className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                        <ShieldOff className="w-5 h-5" />
                        Je ne souhaite pas être assuré
                      </div>
                      <p className="text-sm text-gray-600">
                        En cas d'annulation météo, les conditions générales s'appliquent.
                      </p>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Pricing Summary */}
              {total && (
                <div className="border-t pt-6">
                  <Label className="text-lg font-semibold mb-4 block">5. Récapitulatif des prix</Label>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-700">{selectedSlot.slot}</span>
                      <span className="font-semibold">{total.basePrice.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Frais de service</span>
                      <span className="font-semibold">{total.serviceFee.toFixed(2)}€</span>
                    </div>
                    {total.weatherInsurance > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">Assurance météo</span>
                        <span className="font-semibold">{total.weatherInsurance.toFixed(2)}€</span>
                      </div>
                    )}
                    <div className="border-t-2 border-blue-300 pt-3 flex justify-between text-lg">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-blue-900">{total.subtotal.toFixed(2)}€</span>
                    </div>
                    
                    <Alert className="bg-orange-50 border-orange-500">
                      <Info className="h-4 w-4 text-orange-700" />
                      <AlertDescription className="text-orange-900">
                        <div className="font-semibold mb-1">Acompte à régler maintenant : {total.deposit.toFixed(2)}€ (30%)</div>
                        <div className="text-sm">Solde restant : {total.remaining.toFixed(2)}€ à régler le jour de l'embarquement</div>
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              )}
            </>
          )}
        </form>
      </CardContent>

      {selectedSlot && total && (
        <CardFooter className="flex flex-col gap-4 bg-gray-50">
          <Button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white text-lg py-6 shadow-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Redirection vers le paiement...
              </>
            ) : (
              <>
                Payer l'acompte de {total.deposit.toFixed(2)}€ et confirmer
              </>
            )}
          </Button>
          <p className="text-xs text-gray-600 text-center">
            Paiement sécurisé par Stripe • Vos données bancaires sont protégées
          </p>
        </CardFooter>
      )}
    </Card>
  );
};
